// Health and reload bars over everything in a fight, drawn in screen space on
// one Graphics so the cost stays flat however many units are on the field.
// A bar shows once something has taken damage (or is selected); a thin timer
// bar under it fills while a weapon reloads, and turns grey with a cross once
// a defence has fired its last round.
import type { Container, Graphics } from 'pixi.js';
import type { Snapshot, UnitSnapshot } from '@thrash/sim';
import { AIRBORNE, buildingTop, unitTop } from './render.ts';
import { TILE_H } from './iso.ts';
import { is3D } from './view3d.ts';

/** A reload shorter than this is a gun's chatter, not worth a timer bar: one second for defences, more for a soldier's rifle. */
const MIN_RELOAD_TICKS = 10, MIN_UNIT_RELOAD_TICKS = 30;

const ATTACKER = 0x58d36a, DEFENDER = 0xff5a4a, LOW = 0xffc23d;
const RELOAD = 0xffb020, EMPTY = 0x8a8a8a;

function bar(g: Graphics, cx: number, top: number, w: number, frac: number, color: number): void {
  const x = Math.round(cx - w / 2), y = Math.round(top);
  g.rect(x - 1, y - 1, w + 2, 5).fill({ color: 0x000000, alpha: 0.6 });
  g.rect(x, y, Math.max(0, w * frac), 3).fill({ color, alpha: 0.95 });
}

function timer(g: Graphics, cx: number, top: number, w: number, frac: number): void {
  const x = Math.round(cx - w / 2), y = Math.round(top);
  g.rect(x - 1, y - 1, w + 2, 4).fill({ color: 0x000000, alpha: 0.55 });
  g.rect(x, y, w * frac, 2).fill({ color: RELOAD, alpha: 0.95 });
}

function outOfAmmo(g: Graphics, cx: number, top: number, w: number): void {
  const x = Math.round(cx - w / 2), y = Math.round(top);
  g.rect(x - 1, y - 1, w + 2, 4).fill({ color: EMPTY, alpha: 0.8 });
  g.moveTo(cx - 3, y - 4).lineTo(cx + 3, y + 4).moveTo(cx + 3, y - 4).lineTo(cx - 3, y + 4).stroke({ color: 0xff5050, width: 1.5 });
}

/** The attacker's bar goes from green through amber as it weakens; the defender's stays red, so sides read at a glance. */
function hpColor(frac: number, defender: boolean): number {
  if (defender) return DEFENDER;
  return frac > 0.5 ? ATTACKER : frac > 0.25 ? LOW : DEFENDER;
}

export interface BarsInput {
  prev: Snapshot;
  next: Snapshot;
  /** Interpolation between prev and next, 0..1. */
  t: number;
  selectedUnits: ReadonlySet<number>;
  selectedBuilding: number;
}

export function paintBars(g: Graphics, world: Container, s: BarsInput): void {
  g.clear();
  // the 2D look draws its own health bars in the world; the timers are still drawn here
  const hpHere = is3D();
  const scale = world.scale.x;
  const unitW = Math.round(Math.min(28, Math.max(14, 16 * Math.sqrt(scale))));
  const prevById = new Map<number, UnitSnapshot>();
  for (const u of s.prev.units) prevById.set(u.id, u);
  for (const u of s.prev.patrols) prevById.set(u.id, u);
  const drawUnit = (u: UnitSnapshot): void => {
    const selected = !u.defender && s.selectedUnits.has(u.id);
    const hurt = u.hp < u.maxHp;
    const reloading = u.reload !== undefined && (u.reloadTicks ?? 0) >= MIN_UNIT_RELOAD_TICKS;
    if (!hurt && !selected && !reloading) return;
    const p = prevById.get(u.id) ?? u;
    const [ax, ay] = unitTop(p.x + (u.x - p.x) * s.t, p.y + (u.y - p.y) * s.t);
    const lift = AIRBORNE.has(u.type) ? TILE_H * (is3D() ? 1.8 : 2.2) : 0;
    const g0 = world.toGlobal({ x: ax, y: ay - lift });
    let y = g0.y - 6;
    if (hpHere && (hurt || selected)) { bar(g, g0.x, y, unitW, u.hp / u.maxHp, hpColor(u.hp / u.maxHp, !!u.defender)); y += 5; }
    if (reloading) timer(g, g0.x, y, unitW, 1 - u.reload!);
  };
  for (const u of s.next.units) drawUnit(u);
  for (const u of s.next.patrols) drawUnit(u);
  for (const b of s.next.buildings) {
    if (b.hp <= 0) continue;
    // what the recon drone has found: a crosshair over a marked defence, a warning ring on a found mine
    if (b.marked || b.revealed) {
      const [mx, my] = buildingTop(b);
      const m = world.toGlobal({ x: mx, y: my + 10 });
      reticle(g, m.x, m.y - 10, !!b.revealed);
    }
    const selected = b.id === s.selectedBuilding;
    const hurt = b.hp < b.maxHp;
    // a selected building always shows its timer, however quick its gun: that is how a cooldown is read
    const reloading = b.reload !== undefined && ((b.reloadTicks ?? 0) >= MIN_RELOAD_TICKS || selected);
    if (!hurt && !selected && !reloading && !b.outOfAmmo) continue;
    const [ax, ay] = buildingTop(b);
    const g0 = world.toGlobal({ x: ax, y: ay });
    const w = Math.round(Math.min(64, Math.max(18, b.size * 12 * Math.sqrt(scale))));
    let y = g0.y;
    if (hpHere && (hurt || selected)) { bar(g, g0.x, y, w, b.hp / b.maxHp, DEFENDER); y += 5; }
    if (b.outOfAmmo) outOfAmmo(g, g0.x, y, w);
    else if (reloading) timer(g, g0.x, y, w, 1 - b.reload!);
  }
}

/** A recon mark: crosshairs over a watched defence, an amber ring over a mine found. */
function reticle(g: Graphics, x: number, y: number, mine: boolean): void {
  const c = mine ? LOW : 0xff3030;
  g.circle(x, y, 6).stroke({ color: c, width: 1.5, alpha: 0.9 });
  if (mine) return;
  g.moveTo(x - 9, y).lineTo(x - 3, y).moveTo(x + 3, y).lineTo(x + 9, y)
    .moveTo(x, y - 9).lineTo(x, y - 3).moveTo(x, y + 3).lineTo(x, y + 9).stroke({ color: c, width: 1.5, alpha: 0.9 });
}

/** The box being dragged out to select units. */
export function paintSelectBox(g: Graphics, x0: number, y0: number, x1: number, y1: number): void {
  const x = Math.min(x0, x1), y = Math.min(y0, y1), w = Math.abs(x1 - x0), h = Math.abs(y1 - y0);
  g.rect(x, y, w, h).fill({ color: ATTACKER, alpha: 0.12 }).stroke({ color: ATTACKER, width: 1.5, alpha: 0.9 });
}

/** Where an order was given: a ring that shrinks away. Green for a move, red for an attack. */
export function paintPing(g: Graphics, sx: number, sy: number, age: number, attack: boolean): void {
  if (age >= 1) return;
  const r = 14 * (1 - age) + 3;
  g.ellipse(sx, sy, r, r / 2).stroke({ color: attack ? DEFENDER : ATTACKER, width: 2, alpha: 1 - age });
}
